import React, { useEffect, useId, useRef, useState } from "react";

// Renders a Mermaid flow/structure diagram from agent-emitted source (#10250).
// The agentic `create_diagram` tool streams Mermaid text (e.g. `flowchart TD`);
// this turns it into inline SVG. Mermaid is loaded lazily (dynamic import) so it
// stays out of the main bundle, and a parse failure renders a contained error
// instead of throwing — a malformed diagram must never blank or crash the
// surrounding chat message.
//
// `securityLevel: "strict"` sanitizes labels (no raw HTML) and falls back to
// wrapped SVG text, which also sizes each node to its label — directly fixing the
// "text overflows the box" complaint that motivated this feature.

interface DiagramViewerProps {
  diagram: string;
  title?: string;
  /** Drop the default 36rem height cap — for hosts that scroll the diagram
   *  themselves (the artifact side pane, its full-screen view). */
  unbounded?: boolean;
}

const DiagramViewer = ({ diagram, title, unbounded }: DiagramViewerProps) => {
  // useId is stable across renders but contains characters Mermaid can't use in
  // a DOM id; strip them to a safe slug.
  const renderId = `mermaid-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Guards against a stale async render writing over a newer one.
  const latest = useRef(0);

  useEffect(() => {
    const token = ++latest.current;
    setFailed(false);
    setSvg(null);

    let cancelled = false;
    void (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "neutral",
        });
        // `parse` throws on invalid source before we attempt a render.
        await mermaid.parse(diagram);
        // Mermaid lays the diagram out in a scratch element to measure it,
        // and by default appends that to the end of the body, in the page's
        // flow: for a moment the page is taller than the window, a
        // scrollbar takes 8px, and everything on screen shifts and shifts
        // back (Gerry, Oct 5: "switching between artifacts causes a
        // jump"). It gets a scratch of its own out of the flow: fixed, off
        // screen and unseen, but still laid out so the measuring works.
        const scratch = document.createElement("div");
        scratch.setAttribute("aria-hidden", "true");
        scratch.style.cssText =
          "position:fixed;left:-10000px;top:0;width:1200px;visibility:hidden;pointer-events:none;";
        document.body.appendChild(scratch);
        let rendered: string;
        try {
          ({ svg: rendered } = await mermaid.render(
            renderId,
            diagram,
            scratch
          ));
        } finally {
          scratch.remove();
        }
        if (!cancelled && token === latest.current) {
          setSvg(rendered);
        }
      } catch {
        if (!cancelled && token === latest.current) {
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [diagram, renderId]);

  if (failed) {
    return (
      <div
        role="alert"
        className="my-2 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600"
      >
        This diagram couldn&apos;t be rendered.
      </div>
    );
  }

  return (
    <div className="w-full">
      {title && (
        <div className="mb-2 text-center text-sm font-medium text-gray-700">
          {title}
        </div>
      )}
      {svg ? (
        // Mermaid output is sanitized SVG (securityLevel "strict"); the centered,
        // overflow-auto box keeps a large diagram contained without clipping.
        <div
          className={`flex w-full justify-center overflow-auto [&_svg]:max-w-full [&_svg]:h-auto ${
            unbounded ? "" : "max-h-[36rem]"
          }`}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="h-24 w-full animate-pulse rounded-md bg-gray-100" />
      )}
    </div>
  );
};

export default DiagramViewer;
